import React, { useEffect, useState, useCallback } from 'react';
import { fetchPrometheusMetrics } from '../services/prometheusService';
import OperatorCard from './OperatorCard';
import MasterChefCard from './MasterChefCard';
import NetdataCard from './NetdataCard';
import HaproxyCard from './HaproxyCard';
import EnvioCard from './EnvioCard';
import StatusIndicator from './StatusIndicator';

interface DeploymentData {
    headBlock: number;
    delta: number;
    eta: number;
    syncSpeed: number;
}

export interface DockerContainer {
    name: string;
    image: string;
    state: 'running' | 'paused' | 'exited' | 'created' | 'restarting' | 'removing' | 'dead';
    health: 'healthy' | 'unhealthy' | 'not_running_unhealthy' | 'starting' | 'no_healthcheck';
}

interface GraphServerData {
    chainHeadCacheLatestBlock: number;
    deploymentHeads: Record<string, DeploymentData>;
    erpc_upstream_block_head_lag?: number;
    erpc_upstream_finalization_lag?: number;
    erpc_upstream_finalized_block_number?: number;
    erpc_upstream_latest_block_number?: number;
    system?: {
        disks?: { space: { used: number; available: number } }[];
    };
    docker_containers?: DockerContainer[];
}

interface GraphServer {
    name: string;
    url: string;
}

interface NetdataMetrics {
    serverName: string;
    docker_containers: DockerContainer[];
    disks: { space: { used: number; available: number } }[];
}

const graphServers: GraphServer[] = [
    {
        name: 'Sonic: Graph-7',
        url: 'https://metro-subgraph3.b-cdn.net/'
    },
    {
        name: 'Envio 1',
        url: 'https://metro-subgraph6.b-cdn.net/'
    },
    {
        name: 'Base: Graph-6',
        url: 'https://base-subgraph3.b-cdn.net/'
    },
    {
        name: 'Base: Graph-5',
        url: 'https://base-subgraph-5.b-cdn.net/'
    },
    {
        name: "Sonic Graph 8",
        url: "https://sonic-subgraph-8.b-cdn.net/"
    },
    {
        name: "Sonic Graph 9",
        url: "https://sonic-subgraph-9.b-cdn.net/"
    }
];

const netdataServers: any[] = [
    {
        name: 'netdata-analytics-graph7',
        url: 'https://netdata-analytics-graph3.b-cdn.net/api/v1/allmetrics?format=prometheus'
    },
    {
        name: 'netdata-analytics-graph6',
        url: 'https://netdata-analytics-graph6.b-cdn.net/api/v1/allmetrics?format=prometheus'
    },
    {
        name: 'netdata-analytics-base-graph3',
        url: 'https://netdata-analytics-base-graph3.b-cdn.net/api/v1/allmetrics?format=prometheus'
    },
    {
        name: 'netdata-analytics-base-graph5',
        url: 'https://netdata-analytics-base5.b-cdn.net/api/v1/allmetrics?format=prometheus'
    }
];

interface EnvioServer {
    name: string;
    url: string;
    useProxy?: boolean;
    proxyUrl?: string;
}

const envioServers: EnvioServer[] = [
    {
        name: 'Envio 1',
        url: 'https://envio-1.b-cdn.net/api/rest/lastBlock',
        useProxy: false // Set to true if CORS is not configured in Bunny CDN

    }
];

// CSS styles
const styles = {
    dashboardTabs: {
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        borderBottom: '1px solid var(--metro-card-border)'
    },
    tab: {
        background: 'none',
        border: 'none',
        color: 'var(--metro-text-secondary)',
        padding: '12px 16px',
        fontSize: '16px',
        cursor: 'pointer',
        position: 'relative' as const,
        transition: 'color 0.3s ease'
    },
    tabHover: {
        color: 'var(--metro-text)'
    },
    activeTab: {
        color: 'var(--metro-accent)',
        fontWeight: 500
    },
    activeTabAfter: {
        content: '""',
        position: 'absolute' as const,
        bottom: '-1px',
        left: 0,
        width: '100%',
        height: '2px',
        background: 'var(--metro-gradient)'
    },
    overviewStats: {
        display: 'flex',
        gap: '24px',
        marginBottom: '24px',
        flexWrap: 'wrap' as const
    },
    statCard: {
        backgroundColor: 'var(--metro-card-bg)',
        border: '1px solid var(--metro-card-border)',
        borderRadius: 'var(--card-radius)',
        padding: '20px',
        flex: 1,
        minWidth: '200px'
    },
    statTitle: {
        color: 'var(--metro-text-secondary)',
        fontSize: '14px',
        marginBottom: '8px'
    },
    statValue: {
        color: 'var(--metro-text)',
        fontSize: '24px',
        fontWeight: 600
    },
    serverHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },
    serverUrl: {
        color: 'var(--metro-text-secondary)',
        fontSize: '14px'
    },
    chainHeadIcon: {
        marginRight: '8px',
        color: 'var(--metro-accent)'
    },
    deploymentId: {
        fontFamily: 'monospace',
        backgroundColor: 'var(--metro-dark-secondary)',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px'
    },
    tableContainer: {
        overflowX: 'auto' as const
    },
    loadingAnimation: `
        @keyframes dash {
            0% {
                stroke-dashoffset: 113;
                transform: rotate(0deg);
            }
            50% {
                stroke-dashoffset: 30;
            }
            100% {
                stroke-dashoffset: 113;
                transform: rotate(360deg);
            }
        }
    `
};

const Dashboard: React.FC = () => {
    const [serverData, setServerData] = useState<Record<string, GraphServerData>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<string>('subgraphs');
    const [systemStatus, setSystemStatus] = useState({
        dockerStatus: { running: 0, total: 0 },
        diskStatus: { critical: 0, warning: 0, total: 0 }
    });
    const [netdataMetrics, setNetdataMetrics] = useState<NetdataMetrics[]>([]);

    const fetchData = async () => {
        const newServerData: Record<string, GraphServerData> = {};
        const newErrors: Record<string, string> = {};

        for (const server of graphServers) {
            try {
                const data = await fetchPrometheusMetrics(server.url);
                newServerData[server.name] = data;
            } catch (err) {
                newErrors[server.name] = 'Failed to fetch metrics';
                console.error(`Error fetching from ${server.name}:`, err);
            }
        }

        setServerData(newServerData);
        setErrors(newErrors);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
        return () => clearInterval(interval);
    }, []);

    // Memoized callback to prevent unnecessary re-renders
    const handleNetdataMetrics = useCallback((serverName: string, docker_containers: DockerContainer[], disks: { space: { used: number; available: number } }[]) => {
        setNetdataMetrics(prev => {
            const filtered = prev.filter(m => m.serverName !== serverName);
            return [...filtered, { serverName, docker_containers, disks }];
        });
    }, []);

    useEffect(() => {
        const calculateSystemStatus = () => {
            let runningContainers = 0;
            let totalContainers = 0;
            let criticalDisks = 0;
            let warningDisks = 0;
            let totalDisks = 0;

            // Use netdataMetrics for status
            netdataMetrics.forEach(metrics => {
                metrics.disks?.forEach(disk => {
                    if (disk.space) {
                        totalDisks++;
                        const usedPercentage = (disk.space.used / (disk.space.used + disk.space.available)) * 100;
                        if (usedPercentage >= 90) {
                            criticalDisks++;
                        } else if (usedPercentage >= 80) {
                            warningDisks++;
                        }
                    }
                });
                metrics.docker_containers?.forEach(container => {
                    totalContainers++;
                    if (container.state === 'running' && (container.health === 'healthy' || container.health === 'no_healthcheck')) {
                        runningContainers++;
                    }
                });
            });

            setSystemStatus({
                dockerStatus: { running: runningContainers, total: totalContainers },
                diskStatus: { critical: criticalDisks, warning: warningDisks, total: totalDisks }
            });
        };
        calculateSystemStatus();
    }, [netdataMetrics]);

    if (loading && Object.keys(serverData).length === 0) {
        return (
            <div className="dashboard" style={{ backgroundColor: 'var(--metro-dark)' }}>
                <div className="loading">
                    <style>
                        {styles.loadingAnimation}
                    </style>
                    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="18" fill="none" strokeWidth="4" stroke="rgba(61, 90, 254, 0.1)" />
                        <circle
                            cx="20"
                            cy="20"
                            r="18"
                            fill="none"
                            strokeWidth="4"
                            stroke="var(--metro-accent)"
                            strokeDasharray="113"
                            strokeDashoffset="0"
                            strokeLinecap="round"
                            style={{
                                animation: "dash 1.5s ease-in-out infinite",
                                transformOrigin: "center"
                            }}
                        />
                    </svg>
                    <p>Loading data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <div className="logo-container">
                <img src="/logoMetropolis.png" alt="Metropolis Logo" className="logo" style={{ width: '100px', height: '100px' }} />
                <div className="logo-text">
                    <h1>Metropolis Analytics Dashboard</h1>
                </div>
            </div>

            <StatusIndicator
                dockerStatus={systemStatus.dockerStatus}
                diskStatus={systemStatus.diskStatus}
            />

            <div style={styles.dashboardTabs}>
                <button
                    className={`tab ${activeTab === 'subgraphs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subgraphs')}
                    style={{
                        ...styles.tab,
                        ...(activeTab === 'subgraphs' ? styles.activeTab : {})
                    }}
                >
                    Subgraph Status
                    {activeTab === 'subgraphs' && <span style={styles.activeTabAfter}></span>}
                </button>
                <button
                    className={`tab ${activeTab === 'wallets' ? 'active' : ''}`}
                    onClick={() => setActiveTab('wallets')}
                    style={{
                        ...styles.tab,
                        ...(activeTab === 'wallets' ? styles.activeTab : {})
                    }}
                >
                    Wallet Balances
                    {activeTab === 'wallets' && <span style={styles.activeTabAfter}></span>}
                </button>
            </div>

            <div className="dashboard-content">
                <HaproxyCard
                    url="https://ha-proxy-status-graph-sonic-live.b-cdn.net"
                    username="magicsea"
                    password="magicsea1234!"
                    title="Load Balancer Metropolis Graphs (Old Graphprotocol Graph nodes)"
                    knownServers={[
                        { name: 'sonic-graph-7', ip: '46.166.162.77' },
                        { name: 'sonic-graph-6', ip: '46.166.162.88' },
                    ]}
                />
                <HaproxyCard
                    url="https://ha-proxy-status-envio-live.b-cdn.net"
                    username="magicsea"
                    password="magicsea1234!"
                    title="New overall Graph Load Balancer Sonic/Base/MegaETH"
                    knownServers={[
                        { name: 'envio-1', ip: '46.166.162.77' },
                        { name: 'envio-2', ip: '46.166.162.88' },
                        { name: 'base-graph-5-server', ip: '93.115.29.42' },
                        { name: 'base-graph-6-server', ip: '46.166.160.60' },
                        { name: 'sonic-graph-8-server', ip: '93.115.28.18' },
                        { name: 'sonic-graph-9-server', ip: '93.115.28.13' },

                    ]}
                />
                <HaproxyCard
                    url="https://ha-proxy-status-api-live.b-cdn.net"
                    username="magicsea"
                    password="magicsea1234!"
                    title="Load Balancer Metropolis API Live"
                    knownServers={[
                        { name: 'metropolis-api-1', ip: '84.32.248.49' },
                        { name: 'metropolis-api-2', ip: '84.32.248.32' }
                    ]}
                />

                {envioServers.map((envioServer) => (
                    <EnvioCard
                        key={envioServer.name}
                        name={envioServer.name}
                        url={envioServer.url}
                        refreshInterval={5000}
                        useProxy={envioServer.useProxy}
                        proxyUrl={envioServer.proxyUrl}
                    />
                ))}

                {activeTab === 'subgraphs' && (
                    <>
                        <div style={styles.overviewStats}>
                            <div style={styles.statCard}>
                                <div style={styles.statTitle}>Total Subgraphs</div>
                                <div style={styles.statValue}>{graphServers.length}</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statTitle}>Active Deployments</div>
                                <div style={styles.statValue}>
                                    {Object.values(serverData).reduce((count, data) =>
                                        count + Object.keys(data?.deploymentHeads || {}).length, 0
                                    )}
                                </div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statTitle}>Last Updated</div>
                                <div style={styles.statValue}>
                                    {new Date().toLocaleTimeString()}
                                </div>
                            </div>
                        </div>

                        {graphServers.map((server, index) => {
                            const data = serverData[server.name];
                            const error = errors[server.name];

                            return (
                                <div key={server.name} className="server-section">
                                    <div style={styles.serverHeader}>
                                        <h2>{server.name}</h2>
                                    </div>
                                    {netdataServers[index] && (
                                        <NetdataCard
                                            netdataUrl={netdataServers[index].url}
                                            serverName={server.name}
                                            onMetrics={handleNetdataMetrics}
                                        />
                                    )}
                                    {error ? (
                                        <div className="error">{error}</div>
                                    ) : data ? (
                                        <>
                                            <div className="">
                                                <h3>
                                                    <span style={styles.chainHeadIcon}>☰</span>
                                                    Chain Head Cache Latest Block: {data.chainHeadCacheLatestBlock}
                                                </h3>
                                            </div>
                                            <div className="deployments">
                                                <h3>Deployment Heads</h3>
                                                <div style={styles.tableContainer}>
                                                    {Object.keys(data.deploymentHeads).length === 0 ? (
                                                        <div style={{
                                                            padding: '20px',
                                                            textAlign: 'center',
                                                            color: 'var(--metro-text-secondary)',
                                                            backgroundColor: 'var(--metro-card-bg)',
                                                            border: '1px solid var(--metro-card-border)',
                                                            borderRadius: 'var(--card-radius)',
                                                            margin: '10px 0'
                                                        }}>
                                                            <p>No active deployments found</p>
                                                            <p style={{ fontSize: '14px', marginTop: '8px' }}>
                                                                This server currently has no running subgraph deployments.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Deployment ID</th>
                                                                    <th>Head Block</th>
                                                                    <th>Delta</th>
                                                                    <th>ETA</th>
                                                                    <th>Progress</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {Object.entries(data.deploymentHeads).map(([deploymentId, deploymentData]) => {
                                                                    const hasHeadBlockData = deploymentData.headBlock > 0;
                                                                    const hasSyncData = deploymentData.syncSpeed > 0;
                                                                    const progress = hasHeadBlockData && data.chainHeadCacheLatestBlock > 0
                                                                        ? (deploymentData.headBlock / data.chainHeadCacheLatestBlock) * 100
                                                                        : 0;
                                                                    const etaDays = Math.floor(deploymentData.eta / (24 * 3600));
                                                                    const etaHours = Math.floor((deploymentData.eta % (24 * 3600)) / 3600);
                                                                    const etaMinutes = Math.floor((deploymentData.eta % 3600) / 60);
                                                                    const etaString = hasSyncData && etaDays < 1
                                                                        ? `${etaHours}h ${etaMinutes}m`
                                                                        : 'N/A';

                                                                    // Determine status based on available data
                                                                    const status = hasSyncData ? 'Active' : hasHeadBlockData ? 'Syncing' : 'Inactive';

                                                                    return (
                                                                        <tr key={deploymentId}>
                                                                            <td>
                                                                                <div style={styles.deploymentId}>{deploymentId}</div>
                                                                            </td>
                                                                            <td>
                                                                                {hasHeadBlockData ? deploymentData.headBlock.toLocaleString() : 'N/A'}
                                                                            </td>
                                                                            <td className={deploymentData.delta > 10 ? 'warning' : ''}>
                                                                                {hasHeadBlockData ? deploymentData.delta.toLocaleString() : 'N/A'}
                                                                            </td>
                                                                            <td>{etaString}</td>
                                                                            <td>
                                                                                {hasHeadBlockData ? (
                                                                                    <>
                                                                                        <div className="progress-text">{progress.toFixed(2)}%</div>
                                                                                        <div className="progress-container">
                                                                                            <div
                                                                                                className={`progress-bar ${progress < 99.9 ? 'warning' : ''}`}
                                                                                                style={{ width: `${progress}%` }}
                                                                                            />
                                                                                        </div>
                                                                                    </>
                                                                                ) : (
                                                                                    <span style={{ color: 'var(--metro-text-secondary)' }}>No data</span>
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                <span style={{
                                                                                    color: hasSyncData ? 'var(--metro-success)' :
                                                                                        hasHeadBlockData ? 'var(--metro-warning)' : 'var(--metro-text-secondary)',
                                                                                    fontSize: '12px'
                                                                                }}>
                                                                                    {status}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="loading">Loading...</div>
                                    )}
                                </div>
                            );
                        })}
                    </>
                )}

                {activeTab === 'wallets' && (
                    <>
                        <h2>Wallet Balances</h2>
                        <div className="wallet-cards">
                            <OperatorCard />
                            <MasterChefCard />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
